import httpClient from '../lib/httpClient';
import {Show, ShowMetaDataType, ShowMetaDataDTO, PlatformType, PaginatedShowsResponse} from '../types/Show';

// Export ShowMetaDataType for use in components
export { ShowMetaDataType } from '../types/Show';

// API prefixes following studio patterns
const apiPrefix = '/api';
const studioApiPrefix = '/studio';
const showApiPrefix = '/show';
const cmsApiPrefix = '/cms';
const genreApiPrefix = '/genre';
const tropeApiPrefix = '/trope';
const tagApiPrefix = '/tag';
const clusterApiPrefix = '/cluster';

// Base URLs for different operations
const SHOW_BASE_URL = `${apiPrefix}${studioApiPrefix}${showApiPrefix}`;

// Types for Internal Details (extending from Show model)
export interface InternalDetailsData {
  actorEthnicity: string;
  sourceSubtitleLanguage: string;
  trailerLinks: string[];
  promoPerformanceDetails: string;
  leadCharacterGender: string;
  targetAudienceGender: string;
  sourceLanguage: string;
}

// Types for Promo Details (from OutsourcedShow model)
export interface Promo {
  id?: string;
  level: string;
  writerName: string;
  producerName: string;
  promosTested: string[];
  bestCpi: number;
  percentMaleInCpi: number;
  bestCpt: number;
  percentMaleInCpt: number;
  promosWithCpiLessThan2: string[];
  promosWithCptLessThan50: string[];
  deleted?: boolean;
}

export interface PromoDetailsData {
  promos: Promo[];
  successfulPromoLinks: string[];
  promoPerformanceDetails: string;
  outsourcedShow?: any;
}

export interface PromoUpdateDTO {
  level?: string;
  writerName?: string;
  producerName?: string;
  promosTested?: string[];
  bestCpi?: number;
  percentMaleInCpi?: number;
  bestCpt?: number;
  percentMaleInCpt?: number;
  promosWithCpiLessThan2?: string[];
  promosWithCptLessThan50?: string[];
}

export interface ReleaseStrategy {
  id: string;
  name: string;
  description: string;
  type: 'FULL' | 'STAGGERED' | string;
  properties: string;
  isDefault: boolean;
  status: 'ACTIVE' | string;
  updatedAt: string;
  createdAt: string;
}

// Types for Dubbing Details (from Dubbing model)
export type LocalizedVersionStatus = "NOT_STARTED" | "IN_PROGRESS" | "ON_HOLD" | "COMPLETED" | "RELEASED";
export type DubbingStage = "SCRIPTING" | "CASTING" | "RECORDING" | "MIXING" | "FINALIZING" | "COMPLETED";
export type WritingStage = "NOT_STARTED" | "FIRST_DRAFT" | "REVIEW" | "REWORKING" | "FINAL_VERSION";
export type QcPassedStatus = "PASSED" | "FAILED" | "PENDING";
export type NameChangeStatus = "NOT_APPLICABLE" | "NOT_STARTED" | "PARTIALLY_COMPLETED" | "COMPLETED";

export interface WriterInfo {
  agencyType: string;
  name: string;
  stages: WritingStage;
  assignedDate?: string;
}

export interface QcInfo {
  type: string;
  qcAgency?: string;
  qcDone: boolean;
  passedStatus: QcPassedStatus;
  owner?: string;
  remarksSheetLink?: string;
}

export interface DubbingInfo {
  studioAssigned: string;
  assignedDate: string; // ISO 8601 format
  stages: DubbingStage;
  notes?: string;
}

export interface NameChangeInfo {
  changedToLocalizedName?: string;
  status: NameChangeStatus;
}

export interface LocalizedVersion {
  language: string;
  status: LocalizedVersionStatus;
  dubbingInfo?: DubbingInfo;
  writerInfo?: WriterInfo;
  qcInfo?: QcInfo;
  dubbedLinkFinal?: string;
  localizationSheetLink?: string;
  nameChangeInfo?: NameChangeInfo;
  missingAdditionalCharacters?: string[];
  localizedEpsDriveLink?: string;
}

export interface DubbingProject {
  id?: string;
  showId: string;
  readyForLocalization: boolean;
  showAssignedDate?: string; // ISO 8601 format
  growthPriority: number | null;
  scrapeStatus: boolean;
  srtStatus: boolean;
  localizedVersions: LocalizedVersion[];
}

export interface DubbingDetailsData {
  projects: DubbingProject[];
}

export interface DubbingProjectCreateDTO {
  showId: string;
  readyForLocalization?: boolean;
  growthPriority?: number | null;
  scrapeStatus?: boolean;
  srtStatus?: boolean;
  localizedVersions?: LocalizedVersion[];
}

export interface DubbingProjectUpdateDTO {
  readyForLocalization?: boolean;
  growthPriority?: number | null;
  scrapeStatus?: boolean;
  srtStatus?: boolean;
  localizedVersions?: LocalizedVersion[];
}

export interface DubbingProjectResponse extends DubbingProject {
  id: string;
  showAssignedDate: string;
}

export interface DubbingProjectDeleteResponse {
  message: string;
  deletedCount: number;
  showId: string;
}


// Show creation types based on provided models
export enum ShowType {
  ONE_SHOT = 'ONE_SHOT',
  SERIALIZED = 'SERIALIZED',
}


export interface ShowDTO {
  name: string;
  creator: string;
  thumbNailImageData: string | null;
  description: string;
  genre: string;
  trope: string;
  createdBy: string | null;
  metadata: ShowMetaDataDTO[];
  type: ShowType;
  platformType: PlatformType.DASHREEL;
  platformTypes?: PlatformType[];
  tag: string;
  cluster: string;
  publisher: string;
  author: string;
  originCountry?: string | null;
  force?: boolean;  // Skip duplicate title check if true
}

// Response types
export interface DuplicateTitleWarning {
  exists: boolean;
  existingShow: Show;  // Non-nullable - if warning exists, existingShow must exist
}

export interface CreateShowResponse {
  show: Show | null;  // Can be null if duplicate found and force=false
  duplicateTitleWarning?: DuplicateTitleWarning | null;
}

// Create new show function (as mentioned by user)
export const createNewShow = async (showData: ShowDTO): Promise<CreateShowResponse> => {
  try {
    return httpClient.post(`${SHOW_BASE_URL}/create`, showData);
  } catch (error) {
    console.error(`Unable to create new show`);
    throw error;
  }
};

// Fetch individual show details (as mentioned by user)
export const fetchShow = async (showId: string, request?: { 
  requestedMetadata?: string[]; 
  includeEpisodes?: boolean; 
  includeEpisodeUserMapping?: boolean; 
}): Promise<Show> => {
  try {
    return httpClient.post(`${SHOW_BASE_URL}/${showId}`, request || {}, {
      resourceId: showId
    });
  } catch (error) {
    console.error(`Unable to fetch show ${showId}`);
    throw error;
  }
};

export const showService = {
  // User Shows API call (using new platform-type endpoint)
  async getUserShows(
    count: number = 20,
    offset: number = 0,
    platformType: PlatformType = PlatformType.DASHREEL,
    nameQuery: string = ''
  ): Promise<Show[]> {
    try {
      const params = new URLSearchParams({ count: String(count), offset: String(offset) });
      const trimmedQuery = nameQuery.trim();
      if (trimmedQuery) {
        params.set('q', trimmedQuery);
      }
      return await httpClient.get(
        `/api/studio/show/platform-type/${platformType}?${params.toString()}`
      );
    } catch (error) {
      console.error(`Unable to fetch user shows`);
      throw error;
    }
  },

  // Internal Details API calls
  async getInternalDetails(showId: string): Promise<InternalDetailsData> {
    try {
      return await httpClient.get(`${SHOW_BASE_URL}/${showId}/internal-details`, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to fetch internal details for show ${showId}`);
      throw error;
    }
  },

  async updateInternalDetails(showId: string, data: InternalDetailsData): Promise<InternalDetailsData> {
    try {
      return await httpClient.put(`${SHOW_BASE_URL}/${showId}/internal-details`, data, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to update internal details for show ${showId}`);
      throw error;
    }
  },

  // Promo Details API calls (using dedicated Studio endpoints)
  async getPromosForShow(showId: string): Promise<Promo[]> {
    try {
      return await httpClient.get(`${SHOW_BASE_URL}/${showId}/promos`, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to get promos for show ${showId}`, error);
      throw error;
    }
  },

  async addPromoToShow(showId: string, promo: Promo): Promise<Promo> {
    try {
      return await httpClient.post(`${SHOW_BASE_URL}/${showId}/addPromo`, promo, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to add promo to show ${showId}`);
      throw error;
    }
  },

  async updatePromoForShow(showId: string, promoId: string, promoUpdateDTO: PromoUpdateDTO): Promise<Promo> {
    try {
      return await httpClient.put(`${SHOW_BASE_URL}/${showId}/updatePromo/${promoId}`, promoUpdateDTO, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to update promo ${promoId} for show ${showId}`);
      throw error;
    }
  },

  async deletePromoFromShow(showId: string, promoId: string): Promise<{ deleted: boolean }> {
    try {
      return await httpClient.delete(`${SHOW_BASE_URL}/${showId}/promo/${promoId}`, null, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to delete promo ${promoId} from show ${showId}`);
      throw error;
    }
  },

  async getSuccessfulPromoLinks(showId: string): Promise<string[]> {
    try {
      // Get successful promo links from show metadata
      const show = await fetchShow(showId, {
        requestedMetadata: ['SUCCESSFUL_PROMOS'],
        includeEpisodes: false,
        includeEpisodeUserMapping: false
      });
      
      const linksMetadata = show.metadata?.find(m => m.type === ShowMetaDataType.SUCCESSFUL_PROMOS);
      if (linksMetadata?.value) {
        try {
          return JSON.parse(linksMetadata.value);
        } catch {
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error(`Unable to get successful promo links for show ${showId}`);
      throw error;
    }
  },

  async updateSuccessfulPromoLinks(showId: string, links: string[]): Promise<string[]> {
    try {
      // Update successful promo links in show metadata
      await updateShowMetadata(showId, [
        { type: ShowMetaDataType.SUCCESSFUL_PROMOS, value: JSON.stringify(links) }
      ]);

      return links;
    } catch (error) {
      console.error(`Unable to update successful promo links for show ${showId}`);
      throw error;
    }
  },
  async getPromoPerformanceDetails(showId: string): Promise<string> {
    try {
      // Get promo performance details from show metadata
      const show = await fetchShow(showId, {
        requestedMetadata: ['PROMO_PERFORMANCE_DETAILS'],
        includeEpisodes: false,
        includeEpisodeUserMapping: false
      });

      const performanceDetailsMetadata = show.metadata?.find(m => m.type === ShowMetaDataType.PROMO_PERFORMANCE_DETAILS);
      return performanceDetailsMetadata?.value || '';
    } catch (error) {
      console.error(`Unable to get promo performance details for show ${showId}`);
      throw error;
    }
  },
  async updatePromoPerformanceDetails(showId: string, details: string): Promise<string> {
    try {
      // Update promo performance details in show metadata
      await updateShowMetadata(showId, [
        { type: ShowMetaDataType.PROMO_PERFORMANCE_DETAILS, value: details }
      ]);

      return details;
    } catch (error) {
      console.error(`Unable to update promo performance details for show ${showId}`);
      throw error;
    }
  },

  // Outsourced Show API calls
  async getOutsourcedShow(outsourcedShowId: string): Promise<any> {
    try {
      return await httpClient.get(`/api/studio/outsourced-shows/${outsourcedShowId}`);
    } catch (error) {
      console.error(`Unable to get outsourced show ${outsourcedShowId}:`, error);
      throw error;
    }
  },

  async checkAndFetchOutsourcedShow(showId: string): Promise<{ mainShow: Show; outsourcedShow?: any }> {
    try {
      // First fetch the main show to check for outsourcedShowId
      const mainShow = await fetchShow(showId, {
        requestedMetadata: ['SUCCESSFUL_PROMOS', 'PROMO_PERFORMANCE_DETAILS'],
        includeEpisodes: false,
        includeEpisodeUserMapping: false
      });

      // If the show has an outsourcedShowId, fetch the outsourced show as well
      if (mainShow.outsourcedShowId) {
        try {
          const outsourcedShow = await showService.getOutsourcedShow(mainShow.outsourcedShowId);
          return { mainShow, outsourcedShow };
        } catch (outsourcedError) {
          console.warn(`Failed to fetch outsourced show ${mainShow.outsourcedShowId}:`, outsourcedError);
          return { mainShow };
        }
      }

      return { mainShow };
    } catch (error) {
      console.error(`Unable to fetch show data for ${showId}:`, error);
      throw error;
    }
  },

  // Dubbing Details API calls (following studio patterns)
  async getDubbingProjects(showId: string): Promise<DubbingProjectResponse[]> {
    try {
      return await httpClient.get(`${SHOW_BASE_URL}/${showId}/dubbing`, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to get dubbing projects for show ${showId}`);
      throw error;
    }
  },

  async createDubbingProject(showId: string, dubbing: DubbingProjectCreateDTO): Promise<DubbingProjectResponse> {
    try {
      return await httpClient.post(`${SHOW_BASE_URL}/${showId}/dubbing`, dubbing, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to create dubbing project for show ${showId}`);
      throw error;
    }
  },

  async updateDubbingProject(showId: string, dubbingId: string, dubbing: DubbingProjectUpdateDTO): Promise<DubbingProjectResponse> {
    try {
      return await httpClient.put(`${SHOW_BASE_URL}/${showId}/dubbing/${dubbingId}`, dubbing, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to update dubbing project ${dubbingId} for show ${showId}`);
      throw error;
    }
  },

  async deleteDubbingProject(showId: string, projectId: string): Promise<DubbingProjectDeleteResponse> {
    try {
      return await httpClient.delete(`${SHOW_BASE_URL}/${showId}/dubbing/${projectId}`, null, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to delete dubbing project ${projectId} for show ${showId}`);
      throw error;
    }
  },

  async deleteAllDubbingProjects(showId: string): Promise<DubbingProjectDeleteResponse> {
    try {
      return await httpClient.delete(`${SHOW_BASE_URL}/${showId}/dubbing`, null, {
        resourceId: showId,
      });
    } catch (error) {
      console.error(`Unable to delete dubbing projects for show ${showId}`);
      throw error;
    }
  }
};

// Transform BytePlus VOD URLs: HTTPS → HTTP (SSL certificate issue workaround)
export const transformVodUrlToHttp = (url: string): string => {
  console.log('🔍 [transformVodUrlToHttp] Input URL:', url);

  // Swap out byteplus for google cdn to load video thumbnails
  var modifiedUrl = url.replace("http://vod.dashreels.com", "https://ingcdn.dashreels.com");
  console.log(modifiedUrl);

  return modifiedUrl;
};

// Fetch show metadata function (as mentioned by user)
export const fetchShowMetadata = async (showId: string, metadataTypes: ShowMetaDataType[]): Promise<Show> => {
  try {
    const show = await httpClient.post(`${SHOW_BASE_URL}/${showId}`, {
      requestedMetadata: metadataTypes.map(type => type.valueOf()),
      includeEpisodes: false,
      includeEpisodeUserMapping: false
    }, {
      resourceId: showId
    });

    // Transform video thumbnail URLs from HTTPS to HTTP (BytePlus SSL issue)
    if (show.thumbnailVariants?.['REELS_VIDEO_THUMBNAIL_9X16']) {
      show.thumbnailVariants['REELS_VIDEO_THUMBNAIL_9X16'] =
        show.thumbnailVariants['REELS_VIDEO_THUMBNAIL_9X16'].map((variant: any) => ({
          ...variant,
          assetId: transformVodUrlToHttp(variant.assetId)
        }));
    }

    // Also transform in metadata array (fallback for old data)
    if (show.metadata) {
      const videoMeta = show.metadata.find((m: any) => m.type === ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16);
      if (videoMeta?.value) {
        try {
          const urls = JSON.parse(videoMeta.value);
          if (Array.isArray(urls)) {
            videoMeta.value = JSON.stringify(urls.map((url: string) => transformVodUrlToHttp(url)));
          }
        } catch (e) {
          console.error('Failed to parse video thumbnail metadata:', e);
        }
      }
    }

    return show;
  } catch (error) {
    console.error(`Unable to fetch show metadata for ${showId}`);
    throw error;
  }
};

// Update show metadata function (as mentioned by user)
// Update show basic details
export const updateShow = async (showId: string, showUpdateData: Partial<ShowDTO>): Promise<Show> => {
  try {
    return httpClient.put(`${SHOW_BASE_URL}/id/${showId}`, showUpdateData, {
      resourceId: showId
    });
  } catch (error) {
    console.error(`Unable to update show ${showId}`);
    throw error;
  }
};

export const updateShowMetadata = async (showId: string, metadataList: ShowMetaDataDTO[]): Promise<any> => {
  try {
    // Studio expects: PUT /studio/show/id/{showId}/metadata with direct array
    return httpClient.put(`${SHOW_BASE_URL}/id/${showId}/metadata`, metadataList, {
      resourceId: showId
    });
  } catch (error) {
    console.error(`Unable to update show metadata for ${showId}`);
    throw error;
  }
};

export const deleteShowMetadata = async (
    showId: string,
    type: ShowMetaDataType,
    silent?: boolean
) => {
  try {
    return httpClient.delete(
        `${studioApiPrefix}${showApiPrefix}/id/${showId}/metadata`,
        type,
        {
          resourceId: showId,
        },
        !silent
    );
  } catch (error) {
    console.error(`Unable to update metadata for show : ${showId} with metadata type : ${type}`);
    return null;
  }
};

// Update thumbnail variant (label and isActive status)
export const updateThumbnailVariant = async (
  showId: string,
  variantId: string,
  label: string,
  isActive: boolean
): Promise<void> => {
  try {
    // Call update label API
    await httpClient.put(
      `${apiPrefix}${studioApiPrefix}${cmsApiPrefix}${showApiPrefix}/${showId}/thumbnail-variant/label`,
      { variantId, label },
      { resourceId: showId }
    );

    // Call update isActive API
    await httpClient.put(
      `${apiPrefix}${studioApiPrefix}${cmsApiPrefix}${showApiPrefix}/${showId}/thumbnail-variant/enabled`,
      { variantId, isActive },
      { resourceId: showId }
    );
  } catch (error) {
    console.error(`Unable to update thumbnail variant ${variantId} for show ${showId}`);
    throw error;
  }
};

// Export promo functions as standalone exports for easier import
export const getPromosForShow = async (showId: string): Promise<Promo[]> => {
  return showService.getPromosForShow(showId);
};

export const addPromoToShow = async (showId: string, promo: Promo): Promise<Promo> => {
  return showService.addPromoToShow(showId, promo);
};

export const updatePromoForShow = async (showId: string, promoId: string, promoUpdateDTO: PromoUpdateDTO): Promise<Promo> => {
  return showService.updatePromoForShow(showId, promoId, promoUpdateDTO);
};

export const deletePromoFromShow = async (showId: string, promoId: string): Promise<{ deleted: boolean }> => {
  return showService.deletePromoFromShow(showId, promoId);
};

export const fetchActiveReleaseStrategies = async (): Promise<ReleaseStrategy[]> => {
  try {
    return httpClient.get('/api/v1/reels-cms/release-strategy/');
  } catch (error) {
    console.error('Unable to fetch active release strategies');
    throw error;
  }
};

// Show state management functions (publish, schedule, etc.)
export const publishShow = async (showId: string) => {
  try {
    return httpClient.post(`${SHOW_BASE_URL}/publish`, {
      showId: showId,
    });
  } catch (error) {
    console.error(`Unable to publish show`);
    throw error;
  }
};

export const scheduleShow = async (showId: string, scheduledDate: string) => {
  try {
    return httpClient.post(`${SHOW_BASE_URL}/schedule`, {
      showId: showId,
      scheduledDate: scheduledDate,
    });
  } catch (error) {
    console.error(`Unable to schedule publish show`);
    throw error;
  }
};

export const unScheduleShow = async (showId: string) => {
  try {
    return httpClient.post(`${SHOW_BASE_URL}/unschedule`, {
      showId: showId,
    });
  } catch (error) {
    console.error(`Unable to unschedule publish show`);
    throw error;
  }
};

export const unpublishShow = async (showId: string) => {
  try {
    return httpClient.post(`${SHOW_BASE_URL}/unpublish`, {
      showId: showId,
    });
  } catch (error) {
    console.error(`Unable to unpublish show`);
    throw error;
  }
};

// Tenant-scoped publish/unpublish goes through the Slack-approval flow
// (useSlackNotification → /api/v1/reels-cms/slack-notification/{showId}); the old
// tenant-publish/tenant-unpublish endpoints never existed in the gateway.

// CMS API functions for fetching genres, tropes, tags, and clusters
export const fetchAvailableGenres = async () => {
  try {
    return httpClient.get(`${cmsApiPrefix}${genreApiPrefix}`);
  } catch (error) {
    console.error(`Unable to fetch available genres`);
    return null;
  }
};

export const fetchAvailableTags = async () => {
  try {
    return httpClient.get(`${cmsApiPrefix}${tagApiPrefix}`);
  } catch (error) {
    console.error(`Unable to fetch available tags`);
    return null;
  }
};

export const fetchAvailableTropes = async () => {
  try {
    return httpClient.get(`${cmsApiPrefix}${tropeApiPrefix}`);
  } catch (error) {
    console.error(`Unable to fetch available tropes`);
    return null;
  }
};

export const fetchAvailableClusters = async () => {
  try {
    return httpClient.get(`${cmsApiPrefix}${clusterApiPrefix}`);
  } catch (error) {
    console.error(`Unable to fetch available cluster`);
    return null;
  }
};

// Country Blacklist API functions
export const fetchShowCountryBlacklist = async (showId: string): Promise<string[]> => {
  try {
    const response = await httpClient.get(`/api/studio/cms/show/${showId}/country-blacklist`);
    return response || [];
  } catch (error) {
    console.error(`Unable to fetch country blacklist for show ${showId}:`, error);
    return [];
  }
};

export const updateShowCountryBlacklist = async (
  showId: string,
  countryCodes: string[]
): Promise<void> => {
  try {
    await httpClient.put(`/api/studio/cms/show/${showId}/country-blacklist`, {
      countryCodes
    });
    console.log(`Successfully updated country blacklist for show ${showId}`);
  } catch (error) {
    console.error(`Unable to update country blacklist for show ${showId}:`, error);
    throw error;
  }
};
